import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";

import { get } from "lib/api";
import { firstMediaUrl } from "lib/media";
import { useAuth } from "context/AuthContext";
import { useCart } from "context/CartContext";
import { currencyForCompany } from "lib/companyProfile";
import useStorefrontHero from "../../shared/useStorefrontHero";
import StorefrontLayout from "../components/StorefrontLayout";
import { resolvePrice, EDS, btnPrimary, btnGhost, btnGhostLight } from "../theme";
import ProductCard from "../components/ProductCard";

const FEATURES = [
  {
    title: "OEM-grade quality",
    text: "Remanufactured to spec with full traceability and QC documentation.",
  },
  {
    title: "Fleet-ready shipping",
    text: "Same-day dispatch on in-stock items with tracking and core return support.",
  },
  {
    title: "Technical support",
    text: "Drivetrain specialists for fitment, diagnostics, and warranty.",
  },
];

export default function HomePage() {
  const navigate = useNavigate();
  const { token, activeCompany, storefrontTenant } = useAuth();
  const { addItem } = useCart();
  const canOrder = !!token;
  const companyKey = String(storefrontTenant || activeCompany || "eds").toLowerCase();
  const { imageUrl: heroImage } = useStorefrontHero(companyKey);

  const { data: productsData, isLoading } = useQuery({
    queryKey: ["eds-home-products", companyKey],
    queryFn: () =>
      get("/api/storefront/catalog", {
        sort: "updatedAt:desc",
        pagination: { pageSize: 8 },
      }),
    staleTime: 30000,
  });

  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    if (!productsData?.data) return;
    setFeaturedProducts(
      productsData.data.map((item) => {
        const attrs = item.attributes || item;
        return {
          id: item.id,
          documentId: item.documentId || item.id,
          sku: attrs.sku,
          name: attrs.name,
          images: attrs.images,
          unitPrice: attrs.unitPrice,
          priceLevels: attrs.priceLevels,
          status: attrs.status,
          transmissions: attrs.transmissions,
        };
      }),
    );
  }, [productsData]);

  const currency = productsData?.meta?.company?.baseCurrency || currencyForCompany(companyKey);

  const openProduct = (product) => {
    navigate(`/catalog/${product.documentId}`);
  };

  const addProduct = (product) => {
    const price = Number(resolvePrice(product) || 0);
    if (!price) return;
    addItem(
      {
        documentId: product.documentId,
        sku: product.sku,
        name: product.name,
        unitPrice: price,
        coreCharge: product.coreCharge != null ? Number(product.coreCharge) : null,
        imageUrl: firstMediaUrl(product?.images),
      },
      1,
    );
  };

  return (
    <StorefrontLayout maxWidth={null}>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          bgcolor: EDS.dark,
          color: "#fff",
          minHeight: { xs: 480, md: 560 },
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(135deg, #0F172A 0%, #1E293B 55%, #0F172A 100%)",
          }}
        />
        {heroImage ? (
          <Box
            sx={{
              position: "absolute",
              right: 0,
              top: 0,
              bottom: 0,
              width: { xs: "55%", md: "45%" },
              opacity: { xs: 0.15, md: 0.55 },
              maskImage: "linear-gradient(to left, black 40%, transparent 100%)",
            }}
          >
            <Box
              component="img"
              src={heroImage}
              alt=""
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </Box>
        ) : null}

        <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1, py: { xs: 6, md: 8 } }}>
          <Box sx={{ maxWidth: 540 }}>
            <Box
              component="p"
              sx={{
                m: 0,
                mb: 1.5,
                fontSize: "0.75rem",
                fontWeight: 400,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              Elite Drive Solutions
            </Box>
            <Box
              component="h1"
              sx={{
                m: 0,
                fontSize: { xs: "2.25rem", md: "3.25rem" },
                fontWeight: 500,
                lineHeight: 1.1,
                letterSpacing: "-0.035em",
              }}
            >
              Drivetrain parts for performance and uptime
            </Box>
            <Box
              component="p"
              sx={{
                mt: 2,
                mb: 0,
                fontSize: "1rem",
                lineHeight: 1.75,
                color: "rgba(255,255,255,0.55)",
                maxWidth: 420,
                fontWeight: 400,
              }}
            >
              Remanufactured transmissions, differentials, and fleet components —
              tested and shipped by a team that knows drivetrain.
            </Box>
            <Box sx={{ mt: 3.5, display: "flex", flexWrap: "wrap", gap: 2 }}>
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/catalog")}
                sx={{
                  ...btnPrimary,
                  bgcolor: "#fff",
                  color: EDS.ink,
                  "&:hover": { opacity: 0.9 },
                }}
              >
                Browse catalog
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => navigate("/register")}
                sx={btnGhostLight}
              >
                Open account
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Grid container spacing={{ xs: 4, md: 0 }}>
          {FEATURES.map((feature, i) => (
            <Grid
              item
              xs={12}
              md={4}
              key={feature.title}
              sx={{
                md: {
                  pl: i > 0 ? 4 : 0,
                  pr: i < FEATURES.length - 1 ? 4 : 0,
                  borderLeft: i > 0 ? `1px solid ${EDS.line}` : "none",
                },
              }}
            >
              <Box
                component="h3"
                sx={{
                  m: 0,
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  color: EDS.ink,
                  letterSpacing: "-0.02em",
                }}
              >
                {feature.title}
              </Box>
              <Box
                component="p"
                sx={{
                  m: "0.5rem 0 0",
                  fontSize: "0.875rem",
                  lineHeight: 1.7,
                  color: EDS.muted,
                  fontWeight: 400,
                  maxWidth: 300,
                }}
              >
                {feature.text}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="xl">
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              gap: 2,
              mb: 4,
            }}
          >
            <Box
              component="h2"
              sx={{
                m: 0,
                fontSize: { xs: "1.375rem", md: "1.625rem" },
                fontWeight: 500,
                letterSpacing: "-0.03em",
                color: EDS.ink,
              }}
            >
              Popular products
            </Box>
            <Box
              component="button"
              type="button"
              onClick={() => navigate("/catalog")}
              sx={{
                margin: 0,
                border: 0,
                padding: 0,
                background: "none",
                color: EDS.link,
                fontWeight: 500,
                cursor: "pointer",
                fontSize: "0.875rem",
                "&:hover": { color: EDS.linkHover },
              }}
            >
              View all
            </Box>
          </Box>

          {isLoading ? (
            <Box sx={{ py: 8, textAlign: "center", color: EDS.muted, fontWeight: 400 }}>
              Loading products…
            </Box>
          ) : featuredProducts.length ? (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {featuredProducts.slice(0, 8).map((product) => (
                <Grid item xs={6} sm={4} md={3} key={product.documentId}>
                  <ProductCard
                    product={product}
                    currency={currency}
                    onOpen={() => openProduct(product)}
                    onAdd={addProduct}
                    canOrder={canOrder}
                  />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ py: 8, textAlign: "center", color: EDS.muted, fontWeight: 400 }}>
              No products listed yet.
            </Box>
          )}
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 }, textAlign: "center" }}>
        <Box
          component="h2"
          sx={{
            m: 0,
            fontSize: { xs: "1.375rem", md: "1.625rem" },
            fontWeight: 500,
            letterSpacing: "-0.03em",
            color: EDS.ink,
          }}
        >
          Ready to order for your shop or fleet?
        </Box>
        <Box
          component="p"
          sx={{
            mt: 1,
            mb: 3,
            color: EDS.muted,
            lineHeight: 1.75,
            fontSize: "0.9375rem",
            fontWeight: 400,
          }}
        >
          Create an account for pricing, core returns, and order history.
        </Box>
        <Box
          component="button"
          type="button"
          onClick={() => navigate(token ? "/account" : "/authentication/sign-in")}
          sx={btnPrimary}
        >
          {token ? "Go to account" : "Sign in to order"}
        </Box>
      </Container>
    </StorefrontLayout>
  );
}
